import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Megaphone, Copy, ExternalLink, Loader2, Save, Users, Upload } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PromoterPageCard = ({ token }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [followers, setFollowers] = useState(0);
  const [exists, setExists] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ handle: '', display_name: '', bio: '', avatar_url: '', cover_url: '', socials: { instagram: '', tiktok: '', x: '', website: '' } });
  const [uploading, setUploading] = useState(null);

  const uploadImage = async (kind, field, file) => {
    if (!file) return;
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', kind);
      const res = await axios.post(`${API}/promoter/profile/upload-image`, fd, { headers });
      setForm(f => ({ ...f, [field]: res.data.url }));
      toast.success(`${kind === 'avatar' ? 'Profile photo' : 'Cover image'} uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setUploading(null); }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/promoter/profile/me`, { headers });
        if (res.data.profile) {
          setForm({ socials: {}, ...res.data.profile });
          setExists(true);
        } else {
          setForm(f => ({ ...f, handle: res.data.suggested_handle || '' }));
          setEditing(true);
        }
        setFollowers(res.data.followers_count || 0);
      } catch { /* not opted in yet */ }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/promoter/profile`, form, { headers });
      setForm({ socials: {}, ...res.data.profile });
      setExists(true);
      setEditing(false);
      toast.success('Promoter page saved — share your link everywhere!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally { setSaving(false); }
  };

  if (loading) return null;
  const pageUrl = `${window.location.origin}/promo/${form.handle}`;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setSocial = (k, v) => setForm(f => ({ ...f, socials: { ...(f.socials || {}), [k]: v } }));

  return (
    <Card className="border-indigo-500/30" data-testid="promoter-page-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-indigo-400" />
            <h2 className="font-bold text-sm">My Promoter Page</h2>
            {exists && <Badge variant="outline" className="text-[10px]"><Users className="h-3 w-3 mr-1" /> {followers} follower{followers === 1 ? '' : 's'}</Badge>}
          </div>
          {exists && !editing && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => { navigator.clipboard.writeText(pageUrl); toast.success('Page link copied — drop it in your bio'); }} data-testid="copy-promoter-link">
                <Copy className="h-3 w-3 mr-1" /> Copy link
              </Button>
              <a href={pageUrl} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="text-xs h-7"><ExternalLink className="h-3 w-3 mr-1" /> View</Button></a>
              <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => setEditing(true)} data-testid="edit-promoter-page">Edit</Button>
            </div>
          )}
        </div>

        {!exists && !editing && null}
        {!editing && exists && (
          <p className="text-xs text-muted-foreground">
            Your evergreen link: <span className="font-mono text-indigo-300">{pageUrl}</span> — every campaign you accept appears there automatically, and your followers get an email each time.
          </p>
        )}

        {editing && (
          <div className="space-y-3" data-testid="promoter-page-form">
            {!exists && (
              <p className="text-xs text-muted-foreground">
                Create your public promoter page — one permanent link for your bio. Fans follow you, and every time you accept a campaign they get an email with your tracked link (you earn on every sale).
              </p>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Handle (your URL)</Label>
                <Input value={form.handle} onChange={e => set('handle', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} placeholder="djkay" data-testid="promoter-handle-input" />
                <p className="text-[10px] text-muted-foreground font-mono">/promo/{form.handle || 'your-handle'}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Display name</Label>
                <Input value={form.display_name || ''} onChange={e => set('display_name', e.target.value)} placeholder="DJ Kay" data-testid="promoter-name-input" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Bio</Label>
              <Textarea value={form.bio || ''} onChange={e => set('bio', e.target.value)} placeholder="Denver nightlife curator. Follow me so you never miss the parties I vouch for." className="min-h-[60px] text-xs" data-testid="promoter-bio-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {[{ kind: 'avatar', label: 'Profile photo', field: 'avatar_url' }, { kind: 'cover', label: 'Cover image (optional)', field: 'cover_url' }].map(cfg => (
                <div className="space-y-1" key={cfg.kind}>
                  <Label className="text-xs">{cfg.label}</Label>
                  <div className="flex items-center gap-2">
                    <div className={`${cfg.kind === 'avatar' ? 'h-10 w-10 rounded-full' : 'h-10 w-16 rounded'} bg-muted overflow-hidden flex-shrink-0 border border-border`}>
                      {form[cfg.field] && <img src={form[cfg.field]} alt="" className="h-full w-full object-cover" />}
                    </div>
                    <label className="cursor-pointer">
                      <span className="inline-flex items-center gap-1 text-xs font-semibold border border-border rounded-md px-2.5 py-1.5 hover:bg-muted" data-testid={`upload-${cfg.kind}-btn`}>
                        {uploading === cfg.kind ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} Upload
                      </span>
                      <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden"
                        onChange={e => uploadImage(cfg.kind, cfg.field, e.target.files?.[0])} data-testid={`upload-${cfg.kind}-input`} />
                    </label>
                    {form[cfg.field] && (
                      <button type="button" className="text-[10px] text-muted-foreground hover:text-red-400" onClick={() => set(cfg.field, '')}>Remove</button>
                    )}
                  </div>
                  {cfg.kind === 'cover' && !form.cover_url && <p className="text-[10px] text-muted-foreground">No cover? Your next event's image fills in automatically.</p>}
                </div>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Instagram</Label>
                <Input value={form.socials?.instagram || ''} onChange={e => setSocial('instagram', e.target.value)} placeholder="@djkay" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Website</Label>
                <Input value={form.socials?.website || ''} onChange={e => setSocial('website', e.target.value)} placeholder="djkay.com" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={save} disabled={saving || !form.handle} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-promoter-page">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                {exists ? 'Save changes' : 'Create my page'}
              </Button>
              {exists && <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PromoterPageCard;
